import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const TALUKAS = ["Latur", "Ausa", "Nilanga", "Udgir", "Ahmedpur", "Chakur", "Renapur", "Deoni", "Shirur Anantpal", "Jalkot"];
const CATEGORIES = ["SC", "ST", "OBC", "VJNT", "SBC", "Open"];
const ELIG = ["Eligible", "Ineligible", "Error / Deficiency", "Correction Submitted", "Verification Pending", "Verified"];
const LOC = ["Taluka Area", "Latur Municipal Corporation Area"];

const empty = {
  application_number: "", full_name: "", guardian_name: "", mother_name: "", dob: "", gender: "Female",
  mobile: "", aadhaar: "", category: "OBC", academic_year: "2025-26", institution: "", course: "",
  class_year: "First Year", taluka: "Latur", district: "Latur", address: "", location_type: "Taluka Area",
  eligibility_status: "Verification Pending",
};

const Field = ({ label, mr, children }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-medium text-slate-600">{label} {mr && <span className="text-slate-400">/ {mr}</span>}</span>
    {children}
  </label>
);
const inputCls = "w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-[#0f2545] focus:outline-none";

export default function BeneficiaryForm() {
  const { bid } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!bid) return;
    api.get(`/beneficiaries/${bid}`).then(({ data }) => {
      setForm({ ...empty, ...Object.fromEntries(Object.keys(empty).map((k) => [k, data[k] ?? ""])) });
    }).catch((e) => toast.error(errMsg(e)));
  }, [bid]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (bid) {
        await api.put(`/beneficiaries/${bid}`, form);
        toast.success("Beneficiary updated successfully");
        navigate(`/beneficiaries/${bid}`);
      } else {
        const { data } = await api.post("/beneficiaries", form);
        toast.success(`Beneficiary ${data.beneficiary_id} created`);
        navigate(`/beneficiaries/${data.beneficiary_id}`);
      }
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="beneficiary-form">
      <h1 className="font-serif text-3xl font-semibold text-[#0f2545] sm:text-4xl">{bid ? `Edit ${bid}` : "Add Beneficiary"}</h1>
      <div className="grid gap-4 rounded-xl border border-slate-200 bg-white p-5 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Application Number" mr="अर्ज क्रमांक"><input data-testid="f-application_number" required value={form.application_number} onChange={set("application_number")} className={inputCls} /></Field>
        <Field label="Full Name" mr="पूर्ण नाव"><input data-testid="f-full_name" required value={form.full_name} onChange={set("full_name")} className={inputCls} /></Field>
        <Field label="Father's / Husband's Name"><input data-testid="f-guardian_name" value={form.guardian_name} onChange={set("guardian_name")} className={inputCls} /></Field>
        <Field label="Mother's Name"><input data-testid="f-mother_name" value={form.mother_name} onChange={set("mother_name")} className={inputCls} /></Field>
        <Field label="Date of Birth"><input data-testid="f-dob" type="date" value={form.dob} onChange={set("dob")} className={inputCls} /></Field>
        <Field label="Gender"><select data-testid="f-gender" value={form.gender} onChange={set("gender")} className={inputCls}>{["Female", "Male", "Other"].map((g) => <option key={g}>{g}</option>)}</select></Field>
        <Field label="Mobile Number" mr="मोबाईल"><input data-testid="f-mobile" required value={form.mobile} onChange={set("mobile")} maxLength={10} className={inputCls} placeholder="10 digits" /></Field>
        <Field label="Aadhaar Number" mr="आधार"><input data-testid="f-aadhaar" required value={form.aadhaar} onChange={set("aadhaar")} maxLength={12} className={inputCls} placeholder="12 digits" /></Field>
        <Field label="Category"><select data-testid="f-category" value={form.category} onChange={set("category")} className={inputCls}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></Field>
        <Field label="Academic Year"><input data-testid="f-academic_year" required value={form.academic_year} onChange={set("academic_year")} className={inputCls} /></Field>
        <Field label="College / Institution" mr="महाविद्यालय"><input data-testid="f-institution" required value={form.institution} onChange={set("institution")} className={inputCls} /></Field>
        <Field label="Course"><input data-testid="f-course" value={form.course} onChange={set("course")} className={inputCls} /></Field>
        <Field label="Class / Year"><input data-testid="f-class_year" value={form.class_year} onChange={set("class_year")} className={inputCls} /></Field>
        <Field label="Taluka" mr="तालुका"><select data-testid="f-taluka" value={form.taluka} onChange={set("taluka")} className={inputCls}>{TALUKAS.map((t) => <option key={t}>{t}</option>)}</select></Field>
        <Field label="Location Type"><select data-testid="f-location_type" value={form.location_type} onChange={set("location_type")} className={inputCls}>{LOC.map((l) => <option key={l}>{l}</option>)}</select></Field>
        <Field label="District"><input data-testid="f-district" value={form.district} onChange={set("district")} className={inputCls} /></Field>
        <Field label="Eligibility Status"><select data-testid="f-eligibility_status" value={form.eligibility_status} onChange={set("eligibility_status")} className={inputCls}>{ELIG.map((s) => <option key={s}>{s}</option>)}</select></Field>
        <Field label="Address" mr="पत्ता"><textarea data-testid="f-address" value={form.address} onChange={set("address")} className={inputCls} rows={2} /></Field>
      </div>
      <div className="flex gap-2">
        <button data-testid="save-beneficiary" disabled={busy} className="rounded-md bg-[#0f2545] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#16345f] disabled:opacity-60">{bid ? "Save Changes" : "Create Beneficiary"}</button>
        <button type="button" onClick={() => navigate(-1)} className="rounded-md border border-slate-300 px-5 py-2.5 text-sm">Cancel</button>
      </div>
    </form>
  );
}
